import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Shield, Key, Database, AlertTriangle, Lock, Server, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { LanguageSwitcher } from '@/components/shared/LanguageSwitcher';

const InformationSecurityPolicy = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Politique de Sécurité de l'Information | EasyFinder</title>
        <meta name="description" content="Politique de Sécurité de l'Information d'EasyFinder : Password Policy, Disaster Recovery et Data Breach Response." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <div className="container max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="-ml-2"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Button>
            <LanguageSwitcher frPath="/information-security-policy" enPath="/information-security-policy-en" />
          </div>

          <div className="space-y-6">
            {/* Header */}
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-full">
                <Shield className="h-8 w-8 text-primary" />
              </div>
              <h1 className="text-3xl font-bold">Politique de Sécurité de l'Information</h1>
              <p className="text-muted-foreground">EasyFinder - SAS au capital de 1 000€</p>
              <div className="flex items-center justify-center gap-2">
                <Badge variant="outline">Version 1.0</Badge>
                <Badge variant="secondary">Dernière mise à jour : Décembre 2024</Badge>
              </div>
            </div>

            <Separator />

            {/* Introduction */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="h-5 w-5 text-primary" />
                  1. Introduction et Objectifs
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm dark:prose-invert max-w-none">
                <p>
                  Cette politique définit les mesures de sécurité mises en œuvre par EasyFinder pour protéger 
                  les données de nos utilisateurs. Elle s'applique à tous les systèmes, employés et sous-traitants 
                  d'EasyFinder.
                </p>
                <h4>Objectifs :</h4>
                <ul>
                  <li>Protéger la confidentialité, l'intégrité et la disponibilité des données</li>
                  <li>Assurer la conformité RGPD et les exigences Google API Limited Use</li>
                  <li>Maintenir la confiance des utilisateurs</li>
                </ul>
              </CardContent>
            </Card>

            {/* Password Policy */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5 text-primary" />
                  2. Politique de Mots de Passe
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm dark:prose-invert max-w-none">
                <p>
                  EasyFinder impose des exigences de mot de passe conformes aux bonnes pratiques de sécurité,
                  avec un indicateur de force en temps réel pour guider les utilisateurs.
                </p>

                <h4>Exigences obligatoires :</h4>
                <ul>
                  <li><strong>8 caractères minimum</strong></li>
                  <li><strong>Au moins une lettre</strong> (a-z, A-Z)</li>
                  <li><strong>Au moins un chiffre</strong> (0-9)</li>
                </ul>

                <h4>Critères bonus (recommandés) :</h4>
                <ul>
                  <li>Au moins une lettre majuscule</li>
                  <li>Au moins un caractère spécial (!@#$%^&*...)</li>
                </ul>

                <h4>Niveaux de force affichés :</h4>
                <div className="not-prose">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                    <div className="p-2 rounded bg-destructive/10 text-destructive text-center text-sm">
                      Incomplet
                    </div>
                    <div className="p-2 rounded bg-amber-500/10 text-amber-600 text-center text-sm">
                      Correct
                    </div>
                    <div className="p-2 rounded bg-emerald-500/10 text-emerald-600 text-center text-sm">
                      Fort
                    </div>
                    <div className="p-2 rounded bg-emerald-600/10 text-emerald-700 text-center text-sm">
                      Très fort
                    </div>
                  </div>
                </div>

                <h4 className="mt-4">Mesures complémentaires :</h4>
                <ul>
                  <li>Transmission chiffrée via HTTPS/TLS 1.3</li>
                  <li>Hashage avec bcrypt (Supabase Auth)</li>
                  <li>Protection contre les attaques par force brute (rate limiting)</li>
                  <li>Tokens JWT avec expiration courte</li>
                </ul>
              </CardContent>
            </Card>

            {/* Data Protection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5 text-primary" />
                  3. Protection des Données
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm dark:prose-invert max-w-none">
                <h4>Chiffrement :</h4>
                <ul>
                  <li><strong>En transit :</strong> TLS 1.3 sur toutes les connexions</li>
                  <li><strong>Au repos :</strong> AES-256 pour les données stockées</li>
                  <li><strong>Tokens OAuth :</strong> AES-256-GCM avec clé de chiffrement dédiée</li>
                </ul>

                <h4>Contrôle d'accès :</h4>
                <ul>
                  <li>Row Level Security (RLS) sur toutes les tables</li>
                  <li>Isolation stricte des données par utilisateur</li>
                  <li>Principe du moindre privilège</li>
                  <li>Audit logs des actions sensibles</li>
                </ul>

                <h4>Accès Gmail (Google API Limited Use) :</h4>
                <ul>
                  <li>Scope readonly uniquement</li>
                  <li>Aucune modification des emails</li>
                  <li>Traitement uniquement des pièces jointes (documents)</li>
                  <li>Tokens révocables à tout moment par l'utilisateur</li>
                </ul>
              </CardContent>
            </Card>

            {/* Disaster Recovery */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="h-5 w-5 text-primary" />
                  4. Disaster Recovery (Continuité d'Activité)
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm dark:prose-invert max-w-none">
                <p>
                  EasyFinder s'appuie sur l'infrastructure Supabase (AWS eu-west-3, Paris) pour garantir 
                  la disponibilité et la récupération des données.
                </p>

                <h4>Objectifs de récupération :</h4>
                <div className="not-prose">
                  <div className="grid md:grid-cols-2 gap-4 mt-2">
                    <div className="p-4 rounded-lg border bg-card">
                      <div className="text-2xl font-bold text-primary">RTO</div>
                      <div className="text-sm text-muted-foreground">Recovery Time Objective</div>
                      <div className="mt-2 font-medium">&lt; 4 heures</div>
                    </div>
                    <div className="p-4 rounded-lg border bg-card">
                      <div className="text-2xl font-bold text-primary">RPO</div>
                      <div className="text-sm text-muted-foreground">Recovery Point Objective</div>
                      <div className="mt-2 font-medium">&lt; 24 heures</div>
                    </div>
                  </div>
                </div>

                <h4 className="mt-4">Sauvegardes :</h4>
                <ul>
                  <li><strong>Base de données :</strong> Snapshots quotidiens automatiques (Supabase)</li>
                  <li><strong>Fichiers (Storage) :</strong> Réplication multi-zone AWS</li>
                  <li><strong>Rétention :</strong> 7 jours de sauvegardes</li>
                  <li><strong>Point-in-Time Recovery :</strong> Disponible sur les 7 derniers jours</li>
                </ul>

                <h4>Procédure de restauration :</h4>
                <ol>
                  <li>Identification de l'incident et évaluation de l'impact</li>
                  <li>Notification aux parties prenantes</li>
                  <li>Restauration depuis le dernier snapshot valide</li>
                  <li>Vérification de l'intégrité des données</li>
                  <li>Remise en service et communication aux utilisateurs</li>
                </ol>
              </CardContent>
            </Card>

            {/* Data Breach Response */}
            <Card className="border-destructive/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                  5. Réponse aux Violations de Données (Data Breach)
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm dark:prose-invert max-w-none">
                <p>
                  En cas de violation de données personnelles, EasyFinder applique une procédure conforme 
                  au RGPD (articles 33 et 34).
                </p>

                <h4>Délais réglementaires :</h4>
                <div className="not-prose">
                  <div className="grid md:grid-cols-2 gap-4 mt-2">
                    <div className="p-4 rounded-lg border border-destructive/30 bg-destructive/5">
                      <div className="font-semibold text-destructive">CNIL</div>
                      <div className="text-sm">Notification sous 72h</div>
                      <div className="text-xs text-muted-foreground mt-1">Si risque pour les droits des personnes</div>
                    </div>
                    <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/5">
                      <div className="font-semibold text-amber-600">Utilisateurs</div>
                      <div className="text-sm">Sans délai</div>
                      <div className="text-xs text-muted-foreground mt-1">Si risque élevé pour les personnes concernées</div>
                    </div>
                  </div>
                </div>

                <h4 className="mt-4">Procédure résumée :</h4>
                <ol>
                  <li><strong>Détection :</strong> Identification et containment immédiat</li>
                  <li><strong>Évaluation :</strong> Nature des données, nombre de personnes, gravité</li>
                  <li><strong>Notification CNIL :</strong> Via <a href="https://notifications.cnil.fr" target="_blank" rel="noopener noreferrer">notifications.cnil.fr</a> sous 72h</li>
                  <li><strong>Communication :</strong> Information des utilisateurs concernés</li>
                  <li><strong>Documentation :</strong> Registre des violations mis à jour</li>
                  <li><strong>Remédiation :</strong> Mesures correctives et préventives</li>
                </ol>

                <h4>Contacts d'urgence :</h4>
                <ul>
                  <li><strong>Équipe Sécurité :</strong> <a href="mailto:security@easyfinder.fr">security@easyfinder.fr</a></li>
                  <li><strong>DPO :</strong> <a href="mailto:privacy@easyfinder.fr">privacy@easyfinder.fr</a></li>
                </ul>

                <p className="text-sm text-muted-foreground">
                  Voir la procédure complète : <a href="/incident-response-policy">Incident Response Policy</a>
                </p>
              </CardContent>
            </Card>

            {/* Roles & Responsibilities */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  6. Rôles et Responsabilités
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm dark:prose-invert max-w-none">
                <div className="not-prose">
                  <div className="space-y-3">
                    <div className="p-3 rounded-lg border bg-card">
                      <div className="font-semibold">Responsable Sécurité (RSSI)</div>
                      <div className="text-sm text-muted-foreground">
                        Définition et application de la politique de sécurité, gestion des incidents
                      </div>
                    </div>
                    <div className="p-3 rounded-lg border bg-card">
                      <div className="font-semibold">Délégué à la Protection des Données (DPO)</div>
                      <div className="text-sm text-muted-foreground">
                        Conformité RGPD, relation avec la CNIL, droits des personnes
                      </div>
                    </div>
                    <div className="p-3 rounded-lg border bg-card">
                      <div className="font-semibold">Équipe Technique</div>
                      <div className="text-sm text-muted-foreground">
                        Implémentation des mesures techniques, monitoring, réponse aux incidents
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Review */}
            <Card>
              <CardContent className="pt-6">
                <div className="text-center space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Cette politique est revue annuellement ou après tout incident de sécurité majeur.
                  </p>
                  <p className="text-sm">
                    <strong>Prochaine révision prévue :</strong> Décembre 2025
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Contact : <a href="mailto:security@easyfinder.fr" className="text-primary hover:underline">security@easyfinder.fr</a>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default InformationSecurityPolicy;
